import React, { useState, useEffect, useCallback } from 'react';
import { Keyboard, X, Sparkles, Navigation, Send, Briefcase, User, Calculator, Video, Star, ArrowUp } from 'lucide-react';

interface ShortcutDef {
  key: string;
  label: string;
  targetId: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  description: string;
  focusTarget?: string;
}

const SHORTCUTS: ShortcutDef[] = [
  { key: 'H', label: 'Home', targetId: '#home', icon: ArrowUp, description: 'Jump to Top / Hero' },
  { key: 'A', label: 'About', targetId: '#about', icon: User, description: 'Read background & story' },
  { key: 'S', label: 'Services', targetId: '#services', icon: Sparkles, description: 'View service packages' },
  { key: 'E', label: 'Estimator', targetId: '#estimator', icon: Calculator, description: 'Interactive project calculator' },
  { key: 'P', label: 'Portfolio', targetId: '#portfolio', icon: Briefcase, description: 'Explore live web projects' },
  { key: 'V', label: 'Videos', targetId: '#ai-videos', icon: Video, description: 'AI shorts & reels showcase' },
  { key: 'T', label: 'Reviews', targetId: '#testimonials', icon: Star, description: 'Client feedback & ratings' },
  { key: 'C', label: 'Contact', targetId: '#contact', icon: Send, description: 'Open contact form & focus input', focusTarget: '#name' },
];

export const KeyboardShortcuts: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeToast, setActiveToast] = useState<{ key: string; label: string } | null>(null);

  const scrollToSection = useCallback((targetId: string, focusTarget?: string) => {
    if (targetId === '#home' || targetId === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      const navOffset = 80;
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      if (focusTarget) {
        setTimeout(() => {
          const inputEl = document.querySelector(focusTarget) as HTMLElement;
          if (inputEl) {
            inputEl.focus();
          }
        }, 500);
      }
    }
  }, []);

  const triggerToast = useCallback((key: string, label: string) => {
    setActiveToast({ key, label });
    setTimeout(() => {
      setActiveToast((current) => (current?.key === key ? null : current));
    }, 1400);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is inside an input, textarea, select, or contenteditable
      const target = e.target as HTMLElement;
      const isInput = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT' || 
        target.isContentEditable;

      // Escape always closes modal
      if (e.key === 'Escape') {
        if (isOpen) {
          e.preventDefault();
          setIsOpen(false);
          return;
        }
      }

      // If user is actively typing in a form field, do not hijack single character keys
      if (isInput) return;

      // Do not hijack browser combinations (Cmd+C, Ctrl+C, Alt, etc.)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const pressedKey = e.key.toUpperCase();

      // Toggle shortcuts modal with '?'
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      // Check matched shortcut
      const match = SHORTCUTS.find((s) => s.key === pressedKey);
      if (match) {
        e.preventDefault();
        scrollToSection(match.targetId, match.focusTarget);
        triggerToast(match.key, match.label);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, scrollToSection, triggerToast]);

  return (
    <>
      {/* Floating HUD Toast Notification when a shortcut is triggered */}
      {activeToast && (
        <div 
          role="status" 
          aria-live="polite"
          className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2.5 px-4 py-2.5 bg-slate-900/90 text-white backdrop-blur-md rounded-full shadow-2xl border border-slate-700/80 animate-in fade-in slide-in-from-bottom-3 duration-200 pointer-events-none"
        >
          <Navigation size={14} className="text-indigo-400 animate-pulse" />
          <span className="text-xs font-medium text-slate-200">
            Navigating to <strong className="text-white font-bold">{activeToast.label}</strong>
          </span>
          <kbd className="px-1.5 py-0.5 bg-indigo-600 text-white rounded text-[10px] font-mono font-bold">
            {activeToast.key}
          </kbd>
        </div>
      )}

      {/* Floating Trigger Pill on Desktop */}
      <button
        onClick={() => setIsOpen(true)}
        aria-label="View Keyboard Shortcuts (Press ?)"
        title="Keyboard Shortcuts (Press ?)"
        className="fixed bottom-6 left-6 z-40 hidden md:flex items-center gap-2 px-3 py-1.5 bg-white/90 hover:bg-white text-slate-700 hover:text-indigo-600 border border-slate-200 rounded-full shadow-sm hover:shadow-md transition-all text-xs font-semibold backdrop-blur-sm group cursor-pointer active:scale-95"
      >
        <Keyboard size={14} className="text-indigo-600 group-hover:rotate-12 transition-transform" />
        <span className="text-[11px] font-bold">Shortcuts</span>
        <kbd className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px] font-mono border border-slate-200">
          ?
        </kbd>
      </button>

      {/* Keyboard Shortcuts Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div 
            className="relative w-full max-w-lg bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 sm:p-8 overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="shortcuts-title"
          >
            {/* Ambient Background Glow */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-100/50 rounded-full blur-3xl pointer-events-none" />

            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                  <Keyboard size={20} />
                </div>
                <div>
                  <h3 id="shortcuts-title" className="text-lg font-bold text-slate-900 tracking-tight">
                    Keyboard Shortcuts
                  </h3>
                  <p className="text-xs text-slate-500">Fast navigation for power users & accessibility</p>
                </div>
              </div>

              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close shortcuts dialog"
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-900 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Shortcuts Grid */}
            <div className="py-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[60vh] overflow-y-auto pr-1">
              {SHORTCUTS.map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.key}
                    onClick={() => {
                      setIsOpen(false);
                      scrollToSection(item.targetId, item.focusTarget);
                      triggerToast(item.key, item.label);
                    }}
                    className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50/80 hover:bg-indigo-50/80 border border-slate-200/80 hover:border-indigo-300 transition-all text-left group cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 group-hover:text-indigo-600 group-hover:border-indigo-200 flex-shrink-0 transition-colors shadow-2xs">
                        <IconComponent size={14} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-bold text-slate-800 group-hover:text-indigo-900 block truncate">
                          {item.label}
                        </span>
                        <span className="text-[10px] text-slate-500 block truncate">
                          {item.description}
                        </span>
                      </div>
                    </div>

                    <kbd className="px-2 py-1 bg-white border border-slate-200 rounded-md text-xs font-mono font-bold text-slate-700 shadow-2xs group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-600 transition-all flex-shrink-0 ml-2">
                      {item.key}
                    </kbd>
                  </button>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <span>Press</span>
                <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-bold text-slate-700">
                  ?
                </kbd>
                <span>anywhere to toggle</span>
              </div>
              <div className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono font-bold text-slate-700">
                  ESC
                </kbd>
                <span>to close</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KeyboardShortcuts;
